import React from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import { WebbingType, ZoneSettings } from '@paragon/contract-library';
import { DistanceField, NumericField } from '@paragon/material';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { LabeledBox } from './LabeledBox';

interface Props {
  readonly zone: ZoneSettings;
  readonly updateZone: (settings: ZoneSettings) => void;
  readonly disabled: boolean;
  readonly hideWebStart?: boolean;
}

export class WebbingSettings extends React.Component<Props> {
  onChange(key: keyof ZoneSettings) {
    return (newValue: number) => {
      this.updateSettings({ [key]: newValue });
    };
  }

  onChangeLeftWebbingStart = (event: React.ChangeEvent<{}>, value: string | null) => {
    if (value === null) return;
    this.updateSettings({ leftWebbingStart: value as ZoneSettings['leftWebbingStart'] });
  };

  onChangeRightWebbingStart = (event: React.ChangeEvent<{}>, value: string | null) => {
    if (value === null) return;
    this.updateSettings({ rightWebbingStart: value as ZoneSettings['leftWebbingStart'] });
  };

  webbingChange = (event: SelectChangeEvent<WebbingType>) => {
    const webbingType = event.target.value as WebbingType;
    if (webbingType !== this.props.zone.webbingType) {
      switch (webbingType) {
        case 'K':
          this.updateSettings({
            maxWebRun: 2,
            horizontalWebLength: this.props.zone.horizontalWebLength,
            webbingType: 'K',
          });
          break;
        case 'Warren':
          this.updateSettings({
            maxWebRun: 4,
            horizontalWebLength: this.props.zone.horizontalWebLength,
            webbingType: 'Warren',
          });
          break;
        default: {
          assertExhaustiveSwitch(webbingType);
        }
      }
    }
  };

  updateSettings(settings: Partial<ZoneSettings>) {
    for (const key of Object.keys(settings) as (keyof ZoneSettings)[]) {
      if (settings[key] !== this.props.zone[key]) {
        this.props.updateZone({ ...this.props.zone, ...settings });
        break;
      }
    }
  }

  renderWebStartOption(label: string, id: string) {
    return (
      <ToggleButton sx={{ width: 75 }} key={id} id={id} value={label} disabled={this.props.disabled}>
        {label}
      </ToggleButton>
    );
  }

  renderWebStart(
    label: string,
    id: string,
    value: string,
    onChange: (event: React.ChangeEvent<{}>, value: string | null) => void,
  ) {
    const webStartOptions = [
      { label: 'Bearing', id: `${id}-bearing` },
      { label: 'Top', id: `${id}-top` },
      { label: 'Bottom', id: `${id}-bottom` },
    ];

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <FormLabel>{label}</FormLabel>
        <ToggleButtonGroup size="small" value={value} exclusive onChange={onChange}>
          {webStartOptions.map((option) => this.renderWebStartOption(option.label, option.id))}
        </ToggleButtonGroup>
      </Box>
    );
  }

  render() {
    return (
      <LabeledBox
        title="Webbing Settings"
        contentSx={{
          display: 'flex',
          flexDirection: 'column',
          padding: 1,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl
            sx={{
              flexBasis: '33%',

              // margins to match text fields in same row
              marginTop: 2,
              marginBottom: 1,
            }}
          >
            <InputLabel htmlFor="default-webbing">Webbing Pattern</InputLabel>
            <Select
              variant="outlined"
              size="small"
              value={this.props.zone.webbingType}
              onChange={this.webbingChange}
              inputProps={{
                name: 'webbing',
                id: 'default-webbing',
              }}
              disabled={this.props.disabled}
              label="Webbing Pattern"
            >
              <MenuItem value="K">K</MenuItem>
              <MenuItem value="Warren">Warren</MenuItem>
            </Select>
          </FormControl>

          <DistanceField
            sx={{ flexBasis: '33%' }}
            label="Horizontal Length"
            value={this.props.zone.horizontalWebLength}
            onSubmit={this.onChange('horizontalWebLength')}
            disabled={this.props.disabled}
          />

          <NumericField
            sx={{ flexBasis: '33%' }}
            label="Max Run"
            value={this.props.zone.maxWebRun}
            onSubmit={this.onChange('maxWebRun')}
            disabled={this.props.zone.webbingType === 'K' || this.props.disabled}
          />
        </Box>

        {this.props.hideWebStart ? null : (
          <Stack spacing={1}>
            {this.renderWebStart(
              'Left Webbing Start',
              'left-webbing-start',
              this.props.zone.leftWebbingStart,
              this.onChangeLeftWebbingStart,
            )}

            {this.renderWebStart(
              'Right Webbing Start',
              'right-webbing-start',
              this.props.zone.rightWebbingStart,
              this.onChangeRightWebbingStart,
            )}
          </Stack>
        )}
      </LabeledBox>
    );
  }
}
